/**
 * ApplicationTracker.js
 * Full Kanban-style job application tracker.
 * Theme-aware (light/dark), self-contained with inline styles.
 */
import React, { useState, useEffect, useCallback } from 'react';

const STAGES = [
  'Wishlist','Applied','Screening','Assessment',
  'Interview','HR Round','Offer','Rejected',
];

const STAGE_META = {
  Wishlist:   { color: '#64748b', bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.25)', icon: '🔖' },
  Applied:    { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',   border: 'rgba(59,130,246,0.25)',  icon: '📤' },
  Screening:  { color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)',   border: 'rgba(139,92,246,0.25)',  icon: '🔍' },
  Assessment: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',   border: 'rgba(245,158,11,0.25)',  icon: '📝' },
  Interview:  { color: '#10b981', bg: 'rgba(16,185,129,0.1)',   border: 'rgba(16,185,129,0.25)',  icon: '🎤' },
  'HR Round': { color: '#06b6d4', bg: 'rgba(6,182,212,0.1)',    border: 'rgba(6,182,212,0.25)',   icon: '🤝' },
  Offer:      { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)',    icon: '🎉' },
  Rejected:   { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.2)',    icon: '✕' },
};

const SOURCES = ['Direct','LinkedIn','Naukri','Indeed','Referral','Campus','Other'];

function daysSince(dateStr) {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function timeAgo(dateStr) {
  const d = daysSince(dateStr);
  if (d === 0) return 'Today';
  if (d === 1) return '1 day ago';
  if (d < 7)  return `${d} days ago`;
  if (d < 30) return `${Math.floor(d/7)} weeks ago`;
  return `${Math.floor(d/30)} months ago`;
}

// ── Application Card ─────────────────────────────────────────────────────────
function AppCard({ app, theme, onMove, onEdit, onDelete }) {
  const T = theme === 'dark';
  const meta = STAGE_META[app.stage] || STAGE_META.Applied;
  const days = daysSince(app.applied_date);
  const needsFollowup = days >= 7 && !['Offer','Rejected','Withdrawn'].includes(app.stage);

  return (
    <div style={{
      background: T ? 'rgba(255,255,255,0.04)' : '#fff',
      border: `1px solid ${T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
      borderRadius: 12, padding: '12px 14px', marginBottom: 8,
      boxShadow: T ? '0 2px 12px rgba(0,0,0,0.25)' : '0 1px 6px rgba(0,0,0,0.07)',
      transition: 'transform 0.15s, box-shadow 0.15s',
      cursor: 'pointer',
      position: 'relative', overflow: 'hidden',
    }}
      onClick={() => onEdit(app)}
      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow=T?'0 4px 20px rgba(0,0,0,0.35)':'0 4px 16px rgba(0,0,0,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow=T?'0 2px 12px rgba(0,0,0,0.25)':'0 1px 6px rgba(0,0,0,0.07)'; }}
    >
      {/* Stage color strip */}
      <div style={{ position:'absolute', top:0, left:0, bottom:0, width:3, background: meta.color, borderRadius:'12px 0 0 12px' }} />

      <div style={{ paddingLeft: 6 }}>
        {/* Company + Role */}
        <div style={{ marginBottom: 6 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: T ? 'rgba(255,255,255,0.9)' : '#0f172a', margin: '0 0 2px' }}>
            {app.company_name}
          </p>
          <p style={{ fontSize: 11, color: T ? 'rgba(255,255,255,0.5)' : '#64748b', margin: 0 }}>
            {app.role_title}
            {app.location ? ` · ${app.location}` : ''}
          </p>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9.5, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
            background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
            {meta.icon} {app.stage}
          </span>
          <span style={{ fontSize: 9, color: T ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
            {timeAgo(app.applied_date)}
          </span>
          {needsFollowup && (
            <span style={{ fontSize: 9, fontWeight: 700, color: '#f59e0b',
              padding: '1px 6px', borderRadius: 10, background: 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.25)' }}>
              ⚡ Follow up
            </span>
          )}
        </div>

        {/* Stage move buttons */}
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }} onClick={e => e.stopPropagation()}>
          {STAGES.indexOf(app.stage) > 0 && (
            <button onClick={() => onMove(app.id, STAGES[STAGES.indexOf(app.stage)-1])}
              style={{ flex:1, padding:'4px', borderRadius:6, border:`1px solid ${T?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.1)'}`,
                background:'transparent', color:T?'rgba(255,255,255,0.4)':'#94a3b8', fontSize:10, cursor:'pointer' }}>
              ← Back
            </button>
          )}
          {STAGES.indexOf(app.stage) < STAGES.length - 1 && (
            <button onClick={() => onMove(app.id, STAGES[STAGES.indexOf(app.stage)+1])}
              style={{ flex:1, padding:'4px', borderRadius:6, border:`1px solid ${meta.border}`,
                background: meta.bg, color: meta.color, fontSize:10, fontWeight:700, cursor:'pointer' }}>
              Advance →
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onDelete(app.id); }}
            style={{ padding:'4px 8px', borderRadius:6, border:'1px solid rgba(239,68,68,0.2)',
              background:'rgba(239,68,68,0.06)', color:'#ef4444', fontSize:10, cursor:'pointer' }}>
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add/Edit Modal ────────────────────────────────────────────────────────────
function AppModal({ app, theme, onSave, onClose }) {
  const T = theme === 'dark';
  const [form, setForm] = useState(app || {
    company_name:'', role_title:'', job_url:'', location:'',
    employment_type:'Full-Time', salary_range:'', source:'LinkedIn',
    stage:'Applied', notes:'', applied_date: new Date().toISOString().split('T')[0],
  });

  const inputStyle = {
    width:'100%', padding:'9px 12px', borderRadius:9, fontSize:12,
    background: T ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    border: `1px solid ${T?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.12)'}`,
    color: T ? 'rgba(255,255,255,0.85)' : '#0f172a',
    outline:'none', boxSizing:'border-box', fontFamily:"'Inter',sans-serif",
  };
  const labelStyle = {
    display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase',
    letterSpacing:'0.5px', color: T?'rgba(255,255,255,0.45)':'#64748b', marginBottom:4,
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(8px)' }} onClick={onClose} />
      <div style={{ position:'relative', width:'100%', maxWidth:500, maxHeight:'90vh', overflowY:'auto',
        borderRadius:20, padding:24, fontFamily:"'Inter',sans-serif",
        background: T ? '#161b2e' : '#fff',
        border: `1px solid ${T?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)'}`,
        boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:16, fontWeight:800, margin:0, color:T?'rgba(255,255,255,0.9)':'#0f172a' }}>
            {app ? '✏️ Edit Application' : '+ Add Application'}
          </h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:T?'rgba(255,255,255,0.4)':'#94a3b8' }}>✕</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          {/* Company */}
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Company Name *</label>
            <input style={inputStyle} placeholder="e.g. Google" value={form.company_name}
              onChange={e=>setForm(f=>({...f, company_name:e.target.value}))} />
          </div>
          {/* Role */}
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Role / Title *</label>
            <input style={inputStyle} placeholder="e.g. Software Engineer" value={form.role_title}
              onChange={e=>setForm(f=>({...f, role_title:e.target.value}))} />
          </div>
          {/* Stage */}
          <div>
            <label style={labelStyle}>Stage</label>
            <select style={{...inputStyle, appearance:'none'}} value={form.stage}
              onChange={e=>setForm(f=>({...f, stage:e.target.value}))}>
              {STAGES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {/* Source */}
          <div>
            <label style={labelStyle}>Source</label>
            <select style={{...inputStyle, appearance:'none'}} value={form.source}
              onChange={e=>setForm(f=>({...f, source:e.target.value}))}>
              {SOURCES.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          {/* Location */}
          <div>
            <label style={labelStyle}>Location</label>
            <input style={inputStyle} placeholder="e.g. Bangalore" value={form.location}
              onChange={e=>setForm(f=>({...f, location:e.target.value}))} />
          </div>
          {/* Salary */}
          <div>
            <label style={labelStyle}>Expected Salary</label>
            <input style={inputStyle} placeholder="e.g. ₹20–25 LPA" value={form.salary_range}
              onChange={e=>setForm(f=>({...f, salary_range:e.target.value}))} />
          </div>
          {/* Job URL */}
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Job URL</label>
            <input style={inputStyle} placeholder="https://linkedin.com/jobs/..." value={form.job_url}
              onChange={e=>setForm(f=>({...f, job_url:e.target.value}))} />
          </div>
          {/* Applied Date */}
          <div>
            <label style={labelStyle}>Applied Date</label>
            <input type="date" style={inputStyle} value={form.applied_date?.split('T')[0] || ''}
              onChange={e=>setForm(f=>({...f, applied_date:e.target.value}))} />
          </div>
          {/* Employment Type */}
          <div>
            <label style={labelStyle}>Type</label>
            <select style={{...inputStyle, appearance:'none'}} value={form.employment_type}
              onChange={e=>setForm(f=>({...f, employment_type:e.target.value}))}>
              {['Full-Time','Part-Time','Contract','Internship','Freelance'].map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          {/* Notes */}
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Notes</label>
            <textarea style={{...inputStyle, resize:'vertical', minHeight:70}} placeholder="Interview rounds, contacts, impressions..."
              value={form.notes} onChange={e=>setForm(f=>({...f, notes:e.target.value}))} />
          </div>
        </div>

        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', borderRadius:10, border:`1px solid ${T?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)'}`,
              background:'transparent', color:T?'rgba(255,255,255,0.5)':'#64748b', fontSize:12.5, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={() => { if (form.company_name && form.role_title) onSave(form); }}
            style={{ flex:2, padding:'11px', borderRadius:10, border:'none',
              background:'linear-gradient(135deg,#3b82f6,#6366f1)', color:'white', fontSize:12.5,
              fontWeight:700, cursor:'pointer', boxShadow:'0 4px 16px rgba(59,130,246,0.3)' }}>
            {app ? 'Save Changes' : '+ Add Application'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Analytics Strip ───────────────────────────────────────────────────────────
function AnalyticsStrip({ analytics, theme }) {
  const T = theme === 'dark';
  const stats = [
    { label:'Total Applied', value: analytics.total,          color:'#3b82f6', icon:'📤' },
    { label:'Active Pipeline', value: analytics.active,       color:'#10b981', icon:'🔥' },
    { label:'Interviews',      value: analytics.by_stage?.Interview || 0, color:'#8b5cf6', icon:'🎤' },
    { label:'Offers',          value: analytics.offers,       color:'#22c55e', icon:'🎉' },
    { label:'Response Rate',   value: `${analytics.response_rate || 0}%`, color:'#f59e0b', icon:'📊' },
  ];
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, marginBottom:20 }}>
      {stats.map(s => (
        <div key={s.label} style={{
          background: T ? 'rgba(255,255,255,0.04)' : '#fff',
          border: `1px solid ${T?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.08)'}`,
          borderRadius:12, padding:'12px 10px', textAlign:'center',
          boxShadow: T?'none':'0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize:20, marginBottom:4 }}>{s.icon}</div>
          <div style={{ fontSize:18, fontWeight:800, color:s.color }}>{s.value}</div>
          <div style={{ fontSize:9, color:T?'rgba(255,255,255,0.35)':'#94a3b8', marginTop:2 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ApplicationTracker({ authToken, theme = 'dark' }) {
  const T = theme === 'dark';
  const [apps, setApps] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');

  const headers = { 'Content-Type':'application/json', Authorization:`Bearer ${authToken}` };

  const fetchAll = useCallback(async () => {
    const hdrs = { 'Content-Type':'application/json', Authorization:`Bearer ${authToken}` };
    try {
      const [appsRes, analyticsRes] = await Promise.all([
        fetch('/api/applications', { headers: hdrs }),
        fetch('/api/applications/analytics', { headers: hdrs }),
      ]);
      if (appsRes.ok) {
        const d = await appsRes.json();
        setApps(d.applications || []);
      }
      if (analyticsRes.ok) {
        const d = await analyticsRes.json();
        setAnalytics(d);
      }
    } catch (e) {
      setError('Failed to load applications.');
    } finally {
      setLoading(false);
    }
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);


  const handleSave = async (form) => {
    const method = editApp ? 'PATCH' : 'POST';
    const url = editApp ? `/api/applications/${editApp.id}` : '/api/applications';
    try {
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (res.ok) {
        setShowModal(false); setEditApp(null);
        fetchAll();
      }
    } catch (e) { console.error(e); }
  };

  const handleMove = async (appId, newStage) => {
    try {
      const res = await fetch(`/api/applications/${appId}/stage`, {
        method: 'POST', headers, body: JSON.stringify({ stage: newStage }),
      });
      if (res.ok) fetchAll();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await fetch(`/api/applications/${appId}`, { method:'DELETE', headers });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (app) => { setEditApp(app); setShowModal(true); };

  // Filter apps to display
  const displayedApps = activeFilter === 'All'
    ? apps
    : apps.filter(a => a.stage === activeFilter);

  // Group by stage
  const byStage = STAGES.reduce((acc, s) => {
    acc[s] = apps.filter(a => a.stage === s);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%',
        color: T?'rgba(255,255,255,0.4)':'#64748b', fontFamily:"'Inter',sans-serif" }}>
        <div>
          <div style={{ fontSize:32, textAlign:'center', marginBottom:12 }}>📋</div>
          <p style={{ fontSize:13 }}>Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden',
      background: T?'#0b0b14':'#f0f2f5', fontFamily:"'Inter',sans-serif" }}>

      {(showModal || editApp) && (
        <AppModal app={editApp} theme={theme} onSave={handleSave}
          onClose={() => { setShowModal(false); setEditApp(null); }} />
      )}

      <div style={{ flex:1, overflowY:'auto', padding:'20px' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div>
            <h2 style={{ fontSize:16, fontWeight:800, margin:0, color:T?'rgba(255,255,255,0.92)':'#0f172a' }}>
              Application Tracker
            </h2>
            <p style={{ fontSize:10, color:T?'rgba(255,255,255,0.4)':'#64748b', margin:'3px 0 0' }}>
              {apps.length} application{apps.length !== 1 ? 's' : ''} tracked
            </p>
          </div>
          <button onClick={() => { setEditApp(null); setShowModal(true); }} style={{
            padding:'9px 16px', borderRadius:10, border:'none',
            background:'linear-gradient(135deg,#3b82f6,#6366f1)',
            color:'white', fontSize:12, fontWeight:700, cursor:'pointer',
            boxShadow:'0 4px 12px rgba(59,130,246,0.3)', display:'flex', alignItems:'center', gap:6,
          }}>
            + Add Job
          </button>
        </div>

        {error && (
          <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.1)',
            border:'1px solid rgba(239,68,68,0.2)', color:'#ef4444', fontSize:11, marginBottom:12 }}>
            {error}
          </div>
        )}

        {/* Analytics */}
        {apps.length > 0 && <AnalyticsStrip analytics={analytics} theme={theme} />}

        {/* Stage filter tabs */}
        <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:4, marginBottom:16 }}>
          {['All',...STAGES].map(s => {
            const cnt = s === 'All' ? apps.length : byStage[s]?.length || 0;
            const active = activeFilter === s;
            const meta = s !== 'All' ? STAGE_META[s] : null;
            return (
              <button key={s} onClick={() => setActiveFilter(s)} style={{
                flexShrink:0, padding:'5px 12px', borderRadius:20, border:`1px solid ${active && meta ? meta.border : T?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)'}`,
                background: active ? (meta?.bg || (T?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)')) : 'transparent',
                color: active ? (meta?.color || (T?'rgba(255,255,255,0.8)':'#0f172a')) : T?'rgba(255,255,255,0.4)':'#64748b',
                fontSize:10.5, fontWeight: active ? 700 : 500, cursor:'pointer',
              }}>
                {meta?.icon} {s} {cnt > 0 && <span style={{ opacity:0.7 }}>({cnt})</span>}
              </button>
            );
          })}
        </div>

        {/* Empty state */}
        {apps.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px 20px' }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🗂️</div>
            <h3 style={{ fontSize:15, fontWeight:800, color:T?'rgba(255,255,255,0.8)':'#0f172a', margin:'0 0 8px' }}>
              No applications yet
            </h3>
            <p style={{ fontSize:11.5, color:T?'rgba(255,255,255,0.35)':'#64748b', margin:'0 0 20px', lineHeight:1.5 }}>
              Start tracking your job applications here.<br/>
              Never lose track of where you applied.
            </p>
            <button onClick={() => setShowModal(true)} style={{
              padding:'11px 24px', borderRadius:10, border:'none',
              background:'linear-gradient(135deg,#3b82f6,#6366f1)',
              color:'white', fontSize:12.5, fontWeight:700, cursor:'pointer',
              boxShadow:'0 4px 16px rgba(59,130,246,0.3)',
            }}>
              + Add Your First Application
            </button>
          </div>
        )}

        {/* Apps list */}
        {displayedApps.length > 0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {displayedApps.map(app => (
              <AppCard key={app.id} app={app} theme={theme}
                onMove={handleMove} onEdit={handleEdit} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {/* No results for filter */}
        {apps.length > 0 && displayedApps.length === 0 && (
          <div style={{ textAlign:'center', padding:'30px', color:T?'rgba(255,255,255,0.35)':'#94a3b8', fontSize:12 }}>
            No applications in <strong>{activeFilter}</strong> stage.
          </div>
        )}
      </div>
    </div>
  );
}
